import { useCallback, useEffect, useState } from 'react';
import { Form, Title, useNotify, useRedirect } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Stack, Button, Alert, AlertTitle, CircularProgress,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import {
  card, btn, typo, NAVY,
  WEBSITE_PUBLICATION_STATE, WEBSITE_FAMILY_LABELS, WEBSITE_MAPPING_REVIEW_STATE,
} from '../styles/ds';
import RecordHeader from '../components/RecordHeader';
import WebsiteListingFields from './WebsiteListingFields';
import WebsiteOfferEditor from './WebsiteOfferEditor';
import WebsiteRelatedProducts from './WebsiteRelatedProducts';
import WebsiteProductImage from './WebsiteProductImage';
import { IssuesTable } from './WebsiteMappingIssues';
import { listingState } from './WebsiteProductList';
import { websiteRequest, hasFixedSize, FAMILY_SELECTION_KEYS } from './websiteApi';

const INITIALS = { simple_tefillin: 'פ', gasot_tefillin: 'ג', mezuzah: 'מ', megillah: 'מג', price_on_request: 'ס' };

/** Publication readiness — the server's own validation, grouped for reading. */
const ValidationPanel = ({ product }) => {
  if (product.retired_at) return null;
  const { errors } = product.validation;
  if (!errors.length) {
    return (
      <Alert severity="success" sx={{ mb: 3 }}>
        {product.publication_state === 'published' ? 'המוצר מפורסם ותקין.' : 'המוצר עומד בכל תנאי הפרסום.'}
      </Alert>
    );
  }
  const grouped = new Map();
  for (const e of errors) grouped.set(e.code, { message: e.message, count: (grouped.get(e.code)?.count || 0) + 1 });
  return (
    <Alert severity="warning" sx={{ mb: 3 }}>
      <AlertTitle>לא ניתן לפרסם עדיין</AlertTitle>
      <Box component="ul" sx={{ m: 0, pr: 2.5, pl: 0 }}>
        {[...grouped.entries()].map(([code, g]) => (
          <li key={code}>{g.message}{g.count > 1 ? ` (${g.count} צירופים)` : ''}</li>
        ))}
      </Box>
    </Alert>
  );
};

const DetailsCard = ({ product, readOnly, onSaved }) => {
  const notify = useNotify();
  const [saving, setSaving] = useState(false);
  const submit = async values => {
    setSaving(true);
    try {
      const updated = await websiteRequest(`/website_products/${product.id}`, 'PUT', {
        category_id: values.category_id, slug: values.slug, name_en: values.name_en,
        script_code: values.script_code, description: values.description,
        additional_information: values.additional_information, availability: values.availability,
        sort_position: values.sort_position,
        fixed_selections: hasFixedSize(product.family) ? { size: values.fixed_selections?.size } : {},
        category_ids: (values.extra_category_ids ?? []).filter(id => id !== values.category_id),
        version: product.version,
      });
      notify('פרטי המוצר נשמרו', { type: 'success' });
      onSaved(updated);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setSaving(false); }
  };
  return (
    <Card sx={card.section}>
      <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
        <Typography sx={{ ...typo.sectionTitle, mb: 2 }}>פרטי המוצר באתר</Typography>
        <Form record={{ ...product, extra_category_ids: product.category_ids.filter(id => id !== product.category_id) }}
          onSubmit={submit} disabled={readOnly}>
          <fieldset disabled={readOnly} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
            <WebsiteListingFields family={product.family} />
          </fieldset>
          {!readOnly && (
            <Button type="submit" variant="contained" disabled={saving} sx={{ ...btn.primary, mt: 1 }}>שמירת פרטים</Button>
          )}
        </Form>
      </CardContent>
    </Card>
  );
};

const ConfirmDialog = ({ open, title, text, confirmLabel, onConfirm, onClose, busy }) => (
  <Dialog open={open} onClose={onClose} dir="rtl" maxWidth="xs" fullWidth>
    <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>{title}</DialogTitle>
    <DialogContent dividers><Typography fontSize={14}>{text}</Typography></DialogContent>
    <DialogActions sx={{ px: 3, py: 2 }}>
      <Button variant="outlined" onClick={onClose} sx={btn.cancel}>ביטול</Button>
      <Button variant="contained" onClick={onConfirm} disabled={busy} sx={btn.primary}>{confirmLabel}</Button>
    </DialogActions>
  </Dialog>
);

const ACTIONS = {
  publish: {
    path: 'publication', body: { action: 'publish' }, done: 'המוצר פורסם באתר',
    title: 'פרסום באתר', text: 'המוצר יוצג ללקוחות באתר (כשהקטגוריה מפורסמת והאתר פעיל). להמשיך?', label: 'פרסום',
  },
  unpublish: {
    path: 'publication', body: { action: 'unpublish' }, done: 'המוצר הוסר מפרסום',
    title: 'הסרה מפרסום', text: 'המוצר יוסתר מהאתר מיד. ניתן לפרסם שוב בהמשך.', label: 'הסרה מפרסום',
  },
  retire: {
    path: 'retire', body: {}, done: 'המוצר הוצא משימוש',
    title: 'הוצאה משימוש',
    text: 'המוצר יוסתר מרשימת המוצרים הפעילים ויהיה לקריאה בלבד. שום נתון לא נמחק, והכתובת (slug) נשארת שמורה לו לצמיתות. הכתב ישתחרר למוצר אתר חדש. בהמשך אפשר להחזיר את המוצר לטיוטה בלבד. להמשיך?',
    label: 'הוצאה משימוש',
  },
  // WD-037: back to Draft only; the server refuses when the script or a mapped
  // variant is now held by another active listing.
  restore: {
    path: 'restore', body: {}, done: 'המוצר הוחזר לטיוטה — יש לאמת מחדש את המיפוי לפני פרסום',
    title: 'החזרה לטיוטה',
    text: 'המוצר יחזור למצב טיוטה עם אותה כתובת (slug), אותם מחירים ואותו מיפוי. אימות המיפוי יאופס, ולכן לפני פרסום יש לבדוק את המחירים והמיפוי ולאמת אותו מחדש. הפעולה תיחסם אם לכתב זה, או לאחד הווריאנטים הממופים, יש כבר מוצר אתר פעיל אחר. להמשיך?',
    label: 'החזרה לטיוטה',
  },
};

const WebsiteProductEdit = () => {
  const { id } = useParams();
  const notify = useNotify();
  const redirect = useRedirect();
  const [product, setProduct] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    websiteRequest(`/website_products/${id}`).then(setProduct).catch(err => setLoadError(err.message));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const runAction = async () => {
    const a = ACTIONS[pending];
    setBusy(true);
    try {
      const updated = await websiteRequest(`/website_products/${product.id}/${a.path}`, 'POST', { ...a.body, version: product.version });
      notify(a.done, { type: 'success' });
      setProduct(updated);
      setPending(null);
    } catch (err) { notify(err.message, { type: 'error' }); }
    finally { setBusy(false); }
  };

  if (loadError) return <Box p={3}><Alert severity="error">{loadError}</Alert></Box>;
  if (!product) return <Box p={6} textAlign="center"><CircularProgress /></Box>;

  const state = listingState(product);
  const retired = !!product.retired_at;
  const published = product.publication_state === 'published';
  const canPublish = !retired && !published && !product.validation.errors.length;
  const openIssues = product.mapping_issues.filter(i => i.status === 'open').length;

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Title title={`מוצר אתר: ${product.name_en}`} />
      <Button startIcon={<ArrowForwardIcon />} onClick={() => redirect('list', 'website_products')} sx={{ mb: 1 }}>
        חזרה לרשימת מוצרי האתר
      </Button>

      <RecordHeader
        initials={INITIALS[product.family] ?? '?'}
        title={<span dir="ltr">{product.name_en}</span>}
        subtitle={`${WEBSITE_FAMILY_LABELS[product.family]}${product.script_code ? ` · כתב ${product.script_code}` : ''} · /products/${product.slug}`}
        chips={[
          { label: WEBSITE_PUBLICATION_STATE[state]?.label, bg: WEBSITE_PUBLICATION_STATE[state]?.bg, color: WEBSITE_PUBLICATION_STATE[state]?.color },
          { label: WEBSITE_MAPPING_REVIEW_STATE[product.mapping_review_state]?.label, bg: WEBSITE_MAPPING_REVIEW_STATE[product.mapping_review_state]?.bg, color: WEBSITE_MAPPING_REVIEW_STATE[product.mapping_review_state]?.color },
        ]}
        meta={[
          { label: 'צירופים ממופים', value: product.configurations.length },
          { label: 'בעיות מיפוי פתוחות', value: openIssues },
          { label: 'גרסה', value: product.version },
        ]}
        actions={retired ? (
          <Button variant="contained" onClick={() => setPending('restore')}
            sx={{ backgroundColor: '#c8a96e', color: NAVY, '&:hover': { backgroundColor: '#b8935a' } }}>
            החזרה לטיוטה
          </Button>
        ) : (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {published
              ? <Button variant="contained" onClick={() => setPending('unpublish')} sx={{ backgroundColor: '#fff', color: NAVY, '&:hover': { backgroundColor: '#eee' } }}>הסרה מפרסום</Button>
              : <Button variant="contained" disabled={!canPublish} onClick={() => setPending('publish')}
                  sx={{ backgroundColor: '#c8a96e', color: NAVY, '&:hover': { backgroundColor: '#b8935a' }, '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.2)', color: 'rgba(255,255,255,0.6)' } }}>
                  פרסום באתר
                </Button>}
            {!published && (
              <Button variant="outlined" onClick={() => setPending('retire')} sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)' }}>הוצאה משימוש</Button>
            )}
          </Stack>
        )}
      />

      {retired && (
        <Alert severity="info" sx={{ mb: 3 }}>
          המוצר הוצא משימוש ב-{new Date(product.retired_at).toLocaleDateString('he-IL')} — מוצג לקריאה בלבד.
          כדי לערוך אותו שוב יש להחזיר אותו לטיוטה (הכתובת נשמרת; המיפוי יאומת מחדש לפני פרסום).
        </Alert>
      )}
      <ValidationPanel product={product} />

      <DetailsCard key={`d${product.version}`} product={product} readOnly={retired} onSaved={setProduct} />

      <WebsiteProductImage product={product} readOnly={retired} onSaved={setProduct} />

      {product.family in FAMILY_SELECTION_KEYS && (
        <WebsiteOfferEditor key={`p${product.version}`} product={product} readOnly={retired} onSaved={setProduct} />
      )}

      <WebsiteRelatedProducts key={`r${product.version}`} product={product} readOnly={retired} onSaved={setProduct} />

      <Card sx={card.section}>
        <CardContent className="crm-card-content" sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography sx={{ ...typo.sectionTitle, mb: 0.5 }}>בעיות מיפוי (WD-035)</Typography>
          <Typography sx={{ ...typo.meta, mb: 2 }}>
            בעיה נפתחת כשווריאנטים ממופים אינם במידה הקבועה של המוצר. המוצר נחסם לפרסום עד שהמיפוי מתוקן והבעיה נסגרת ידנית.
          </Typography>
          {product.mapping_issues.length
            ? <IssuesTable issues={product.mapping_issues} onChanged={load} readOnly={retired} />
            : <Typography fontSize={13} color="text.secondary">אין בעיות מיפוי.</Typography>}
        </CardContent>
      </Card>

      {pending && (
        <ConfirmDialog open title={ACTIONS[pending].title} text={ACTIONS[pending].text} confirmLabel={ACTIONS[pending].label}
          busy={busy} onConfirm={runAction} onClose={() => setPending(null)} />
      )}
    </Box>
  );
};

export default WebsiteProductEdit;
